import api from "./axios";

/**
 * One page of exam attempts → {content, totalElements, totalPages, pageNo}.
 * `rank` on each row is its position in the whole result set, not within the page.
 */
export async function getExamAttempts({examId, studentNumber, pageNo = 1, pageSize = 10}) {
    const res = await api.get("/exam-attempts", {
        params: {examId, studentNumber, pageNo, pageSize},
    });
    return res.data;
}

export async function getExamAttemptLogs(attemptId) {
    const res = await api.get(`/exam-attempts/${Number(attemptId)}/logs`);
    return res.data;
}

export async function getExamAttemptAnswers(attemptId) {
    const res = await api.get(`/exam-attempts/${Number(attemptId)}/answers`);
    return res.data;
}

/** Collapsed subject → topic headers of the review, each with its score. No question content. */
export async function getExamAttemptAnswerGroups(attemptId) {
    const res = await api.get(`/exam-attempts/${Number(attemptId)}/answer-groups`);
    return res.data;
}

/**
 * The answers of ONE subject/topic group, built on demand when that group is opened.
 * Null ids select the bucket of questions that carry no subject/topic.
 */
export async function getExamAttemptAnswersByGroup(attemptId, {subjectId, topicId} = {}) {
    const params = {grouped: true};
    if (subjectId !== null && subjectId !== undefined) params.subjectId = subjectId;
    if (topicId !== null && topicId !== undefined) params.topicId = topicId;
    const res = await api.get(`/exam-attempts/${Number(attemptId)}/answers`, {params});
    return res.data;
}

export async function confirmExamAttempt(attemptId) {
    const res = await api.patch(`/exam-attempts/${Number(attemptId)}/confirm`);
    return res.data;
}

export async function cancelExamAttempt(attemptId, reason) {
    const body = {
        reason: reason,
    };
    const res = await api.patch(`/exam-attempts/${Number(attemptId)}/cancel`, body);
    return res.data;
}

export async function approveOpenAnswer(attemptId, answerId) {
    const res = await api.patch(`/exam-attempts/${Number(attemptId)}/answers/${Number(answerId)}/approve-open`);
    return res.data;
}

export async function rejectOpenAnswer(attemptId, answerId) {
    const res = await api.patch(`/exam-attempts/${Number(attemptId)}/answers/${Number(answerId)}/reject-open`);
    return res.data;
}

export async function confirmAllExamAttempts(examId) {
    const body = {
        examId: Number(examId),
    };
    const res = await api.patch(`/exam-attempts/confirm-all`, body);
    return res.data;
}

/**
 * Download the on-screen exam-attempts table as an .xlsx file.
 * Hits the same backend that powers the page, so every visibility/privilege
 * check there applies (no risk of leaking rows the user can't already see).
 */
export async function downloadExamAttemptsExcel({examId, studentNumber}) {
    const res = await api.get("/exam-attempts/export", {
        params: {examId, studentNumber},
        responseType: "blob",
    });

    // Prefer the filename the server sent in Content-Disposition; fall back to a
    // sensible default if the header isn't exposed (e.g. behind a proxy that strips it).
    const disposition = res.headers?.["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match ? match[1] : `exam-attempts-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}
